import React from 'react'

interface Props {
  children: React.ReactNode
  /** Optional custom fallback. Receives `reset` to try recovering. */
  fallback?: (reset: () => void) => React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Catches React render-tree errors so the whole app doesn't go blank.
 * Wrap at the root (or around specific high-risk subtrees) to provide a
 * recoverable error state instead of an empty white screen.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
    this.reset = this.reset.bind(this)
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface to console so dev tools / Sentry can pick it up
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack)
  }

  reset() {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback(this.reset)
    }

    const { error } = this.state

    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--ds-canvas, #fff)',
          padding: '40px 24px',
          fontFamily: 'var(--ds-font-sans, system-ui, sans-serif)',
        }}
      >
        <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 20, userSelect: 'none' }}>⚡</div>

          <h1
            style={{
              margin: '0 0 10px',
              fontSize: 22,
              fontWeight: 740,
              letterSpacing: '-0.03em',
              color: 'var(--ds-ink, #111)',
            }}
          >
            Something crashed
          </h1>

          <p
            style={{
              margin: '0 0 24px',
              fontSize: 14,
              lineHeight: 1.6,
              color: 'var(--ds-ink-subtle, #666)',
              maxWidth: 340,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            A component hit an unexpected error. Try the actions below — if the problem persists, reload the page.
          </p>

          {error?.message && (
            <code
              style={{
                display: 'block',
                marginBottom: 24,
                padding: '10px 14px',
                borderRadius: 8,
                background: 'var(--ds-hover, #f4f4f5)',
                fontSize: 11.5,
                fontFamily: 'var(--ds-font-mono, monospace)',
                color: 'var(--ds-ink-muted, #444)',
                textAlign: 'left',
                wordBreak: 'break-all',
              }}
            >
              {error.message}
            </code>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={this.reset}
              style={{
                all: 'unset',
                boxSizing: 'border-box',
                height: 40,
                padding: '0 22px',
                borderRadius: 999,
                fontSize: 13.5,
                fontWeight: 640,
                cursor: 'pointer',
                boxShadow: 'inset 0 0 0 1.5px #d1d5db',
                color: 'var(--ds-ink, #111)',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.assign('/')}
              style={{
                all: 'unset',
                boxSizing: 'border-box',
                height: 40,
                padding: '0 22px',
                borderRadius: 999,
                fontSize: 13.5,
                fontWeight: 640,
                cursor: 'pointer',
                background: 'var(--ds-ink, #111)',
                color: 'var(--ds-inverse-ink, #fff)',
              }}
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    )
  }
}
